import React, { useState, useEffect, useMemo } from 'react'

import { SaveOutlined } from '@ant-design/icons'
import {
  Form,
  Radio,
  Input,
  Button,
  Space,
  Typography,
  message,
} from 'antd'
import { useTranslation } from 'react-i18next'

const { Text, Paragraph } = Typography

/**
 * 儲位編碼模板輸入元件
 *
 * @param {Object} props
 * @param {string} props.value - 當前模板值
 * @param {Function} props.onChange - 模板變更回調
 * @param {Function} props.onSave - 儲存為預設模板的回調（可選，如果提供則顯示儲存按鈕）
 * @param {boolean} props.saving - 儲存中狀態
 * @param {boolean} props.showPreview - 是否顯示預覽（預設 true）
 * @param {Object} props.previewParams - 預覽參數 { rowRange, columnRange, layerRange, gridRange }
 * @param {boolean} props.disabled - 是否禁用
 */
export default function ShelfLocationTemplateInput({
  value,
  onChange,
  onSave,
  saving = false,
  showPreview = true,
  previewParams = null,
  disabled = false,
}) {
  const { t } = useTranslation()
  const STANDARD_TEMPLATE = '{row}-{column}-{layer}'

  // 判斷是否為標準模板
  const isStandardTemplate = (template) => {
    return !template || template === STANDARD_TEMPLATE
  }

  // 內部狀態
  const [templateMode, setTemplateMode] = useState(
    isStandardTemplate(value) ? 'standard' : 'custom'
  )
  const [customTemplate, setCustomTemplate] = useState(
    isStandardTemplate(value) ? STANDARD_TEMPLATE : value
  )

  // 當外部 value 變化時同步內部狀態
  useEffect(() => {
    if (isStandardTemplate(value)) {
      setTemplateMode('standard')
    } else {
      setTemplateMode('custom')
      setCustomTemplate(value)
    }
  }, [value])

  // 獲取當前模板
  const getCurrentTemplate = () => {
    return templateMode === 'standard' ? STANDARD_TEMPLATE : customTemplate
  }

  // 處理模式變更
  const handleModeChange = (e) => {
    const newMode = e.target.value
    setTemplateMode(newMode)
    const newTemplate = newMode === 'standard' ? STANDARD_TEMPLATE : customTemplate
    onChange?.(newTemplate)
  }

  // 處理自訂模板變更
  const handleCustomTemplateChange = (e) => {
    const newTemplate = e.target.value
    setCustomTemplate(newTemplate)
    onChange?.(newTemplate)
  }

  // 處理儲存
  const handleSave = async () => {
    if (onSave) {
      await onSave(getCurrentTemplate())
    }
  }

  // 格式化數字（支援補零）
  const formatNumber = (num, format) => {
    const match = format.match(/:0(\d+)d/)
    if (match) {
      const width = parseInt(match[1])
      return String(num).padStart(width, '0')
    }
    return String(num)
  }

  // 檢查模板是否包含 grid 變數
  const templateHasGrid = (template) => {
    return /\{grid(:\d+d)?\}/.test(template)
  }

  // 生成單個 location
  const generateLocation = (row, column, layer, grid = null) => {
    const template = getCurrentTemplate()
    let location = template

    // 替換變數
    location = location.replace(/\{row:0(\d+)d\}/g, (match) => formatNumber(row, match))
    location = location.replace(/\{row\}/g, row)
    location = location.replace(/\{column:0(\d+)d\}/g, (match) => formatNumber(column, match))
    location = location.replace(/\{column\}/g, column)
    location = location.replace(/\{layer:0(\d+)d\}/g, (match) => formatNumber(layer, match))
    location = location.replace(/\{layer\}/g, layer)

    // 替換 grid 變數（即使 grid 為 null，也使用預設值 '1' 來預覽）
    const gridValue = grid !== null ? grid : '1'
    location = location.replace(/\{grid:0(\d+)d\}/g, (match) => formatNumber(gridValue, match))
    location = location.replace(/\{grid\}/g, gridValue)

    return location
  }

  // 預覽範例
  const exampleLocations = useMemo(() => {
    if (!showPreview) return []

    const template = templateMode === 'standard' ? STANDARD_TEMPLATE : customTemplate
    const hasGridInTemplate = templateHasGrid(template)

    // 生成單個預覽 location（內聯函數避免依賴問題）
    const genLocation = (row, column, layer, grid = null) => {
      let location = template

      // 替換變數
      location = location.replace(/\{row:0(\d+)d\}/g, (match) => {
        const m = match.match(/:0(\d+)d/)
        if (m) {
          const width = parseInt(m[1])
          return String(row).padStart(width, '0')
        }
        return String(row)
      })
      location = location.replace(/\{row\}/g, row)

      location = location.replace(/\{column:0(\d+)d\}/g, (match) => {
        const m = match.match(/:0(\d+)d/)
        if (m) {
          const width = parseInt(m[1])
          return String(column).padStart(width, '0')
        }
        return String(column)
      })
      location = location.replace(/\{column\}/g, column)

      location = location.replace(/\{layer:0(\d+)d\}/g, (match) => {
        const m = match.match(/:0(\d+)d/)
        if (m) {
          const width = parseInt(m[1])
          return String(layer).padStart(width, '0')
        }
        return String(layer)
      })
      location = location.replace(/\{layer\}/g, layer)

      // 替換 grid 變數（使用預設值 '1'）
      const gridValue = grid !== null ? grid : '1'
      location = location.replace(/\{grid:0(\d+)d\}/g, (match) => {
        const m = match.match(/:0(\d+)d/)
        if (m) {
          const width = parseInt(m[1])
          return String(gridValue).padStart(width, '0')
        }
        return String(gridValue)
      })
      location = location.replace(/\{grid\}/g, gridValue)

      return location
    }

    // 檢查變數是否使用補零格式（:0Xd = 數字格式，永遠用數字預覽）
    const useNumberPreview = (varName) => {
      return new RegExp(`\\{${varName}:0\\d+d\\}`).test(template)
    }

    // 取得預覽值：補零格式用數字，否則用 previewParams 的值
    const getValues = (range, varName) => {
      if (useNumberPreview(varName)) {
        return ['1', '2']  // 補零格式 = 數字
      }
      const from = range?.from || '1'
      const to = range?.to || '2'
      return [from, to].filter((v, i, arr) => arr.indexOf(v) === i)
    }

    // 如果有提供預覽參數
    if (previewParams) {
      const { rowRange, columnRange, layerRange, gridRange } = previewParams
      const hasGridRange = gridRange?.from && gridRange?.to

      const rows = getValues(rowRange, 'row')
      const columns = getValues(columnRange, 'column')
      const layers = getValues(layerRange, 'layer')
      const grids = hasGridRange
        ? getValues(gridRange, 'grid')
        : (hasGridInTemplate ? ['1', '2'] : [null])

      const examples = []
      outerLoop:
      for (const row of rows) {
        for (const column of columns) {
          for (const layer of layers) {
            for (const grid of grids) {
              if (examples.length >= 5) break outerLoop
              examples.push(genLocation(row, column, layer, grid))
            }
          }
        }
      }
      return examples
    }

    // 預設範例
    return hasGridInTemplate
      ? [genLocation('1', '1', '1', '1'), genLocation('1', '1', '1', '2'), genLocation('1', '1', '2', '1'), genLocation('1', '2', '1', '1')]
      : [genLocation('1', '1', '1'), genLocation('1', '1', '2'), genLocation('1', '2', '1'), genLocation('2', '1', '1')]
  }, [templateMode, customTemplate, previewParams, showPreview])

  return (
    <div>
      <Form.Item label={t('models.shelf.template_mode')}>
        <Radio.Group
          value={templateMode}
          onChange={handleModeChange}
          disabled={disabled}
        >
          <Space direction="vertical">
            <Radio value="standard">
              {t('shared.shelves.batch_new.template_modes.standard')}
              <Text type="secondary" style={{ marginLeft: 8 }}>
                {STANDARD_TEMPLATE} ({t('shared.shelves.batch_new.preview.row')}-{t('shared.shelves.batch_new.preview.column')}-{t('shared.shelves.batch_new.preview.layer')})
              </Text>
            </Radio>
            <Radio value="custom">
              {t('shared.shelves.batch_new.template_modes.custom')}
            </Radio>
          </Space>
        </Radio.Group>
      </Form.Item>

      {templateMode === 'custom' && (
        <Form.Item label={t('models.shelf.custom_template')}>
          <Input
            value={customTemplate}
            onChange={handleCustomTemplateChange}
            placeholder="{row:02d}-{column:02d}-{layer:02d}"
            disabled={disabled}
          />
          <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
            {t('shared.shelves.batch_new.template_help.variables')}
            <br />
            • {'{row}'} - {t('shared.shelves.batch_new.template_help.row')}
            <br />
            • {'{column}'} - {t('shared.shelves.batch_new.template_help.column')}
            <br />
            • {'{layer}'} - {t('shared.shelves.batch_new.template_help.layer')}
            <br />
            • {'{grid}'} - {t('shared.shelves.batch_new.template_help.grid')}
            <br />
            • {'{row:02d}'} - {t('shared.shelves.batch_new.template_help.padded')}
            <br /><br />
            {t('shared.shelves.batch_new.template_help.examples')}
            <br />
            • <Text code>{'A-{row}-{column}-{layer}'}</Text> → A-1-2-3, A-B-C-1
            <br />
            • <Text code>{'{row:02d}-{column:02d}-{layer}'}</Text> → 01-02-3, 0A-0B-1
            <br />
            • <Text code>{'WH-{row}-{column}-{layer}-{grid}'}</Text> → WH-A-1-2-1
          </Paragraph>
        </Form.Item>
      )}

      {showPreview && exampleLocations.length > 0 && (
        <Form.Item label={t('models.shelf.example_preview')}>
          <div style={{
            padding: '12px',
            background: '#f5f5f5',
            borderRadius: '4px',
            fontFamily: 'monospace'
          }}>
            {exampleLocations.map((location, idx) => (
              <div key={idx}>{location}</div>
            ))}
          </div>
        </Form.Item>
      )}

      {onSave && (
        <Form.Item>
          <Button
            onClick={handleSave}
            loading={saving}
            icon={<SaveOutlined />}
            disabled={disabled}
          >
            {t('shared.shelves.batch_new.template.save_as_default')}
          </Button>
        </Form.Item>
      )}
    </div>
  )
}
